import { useFeatureFlagEnabled } from "@posthog/react";
import { LENDER_MARKETPLACE_FLAG_KEY } from "../lib/feature-flags";
import { IS_PREVIEW, previewQueryParam } from "../lib/pro-mode";
import { useProfileStore } from "../stores/profile";

export type LenderMarketplaceGate = "loading" | "off" | "on";

/**
 * Role-aware lender gate layered under the marketplace go-live gate.
 *
 * Agents are always `on` here (they follow `marketplace_go_live_enabled`
 * alone). A loan officer is `on` only when `lender_marketplace_enabled`
 * resolves true. The role comes from the professional profile store (the
 * server's `/api/professional/profile`), never Clerk metadata. Until the role
 * resolves the answer is `loading`, so no marketplace request can fire for a
 * lender whose role is not yet known. DEV builds short-circuit to `on`, like
 * the go-live gate. Preview: `?role=lo` simulates a lender (`&lender=on` for
 * the flag on).
 */
export function useLenderMarketplaceGate(): LenderMarketplaceGate {
  const flag = useFeatureFlagEnabled(LENDER_MARKETPLACE_FLAG_KEY);
  const role = useProfileStore((s) => s.data?.role ?? null);
  if (IS_PREVIEW && previewQueryParam("role") === "lo") {
    return previewQueryParam("lender") === "on" ? "on" : "off";
  }
  if (import.meta.env.DEV) return "on";
  if (role === null) return "loading";
  if (role !== "lo") return "on";
  if (flag === undefined) return "loading";
  return flag === true ? "on" : "off";
}

/**
 * The raw lender launch flag, for visitors with no role yet (the `/join`
 * picker): loading counts as off. Preview: `?lender=on` simulates it on.
 */
export function useLenderFlagOn(): boolean {
  const flag = useFeatureFlagEnabled(LENDER_MARKETPLACE_FLAG_KEY);
  return IS_PREVIEW ? previewQueryParam("lender") === "on" : flag === true;
}
